import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { matchPathname } from '../../util/routes';
import { readSearchState } from '../../util/unifiedSearch';

import { FALLBACK_CATEGORIES, resolveGroups } from '../CategoryShortcuts/categoryGroups';

/**
 * The three space-type groups of the hosted category tree (fallback: the marketplace's own ids
 * while the hosted assets are missing). Keyed on the configuration object, not on a `?? []`
 * fallback — a fresh array per render would recompute every count URL downstream.
 *
 * @returns {Array} resolved groups ({ key, ids, Icon, labelId })
 */
export const useSpaceTypeGroups = () => {
  const config = useConfiguration();
  const categoryConfiguration = config?.categoryConfiguration;
  return useMemo(() => {
    const hosted = categoryConfiguration?.categories;
    return resolveGroups(hosted?.length > 0 ? hosted : FALLBACK_CATEGORIES);
  }, [categoryConfiguration]);
};

/**
 * The applied search of the current page, read from the route and the URL only.
 *
 * Pure on the URL: the server render and the first client render read the same location and get
 * the same answer (SSR & hydration rules in CLAUDE.md). The draft of an open editor lives in the
 * editor, never here.
 *
 * @returns {{ state: Object, groups: Array, routeName: string|null, pathParams: Object,
 *   location: Object }}
 */
const useSearchState = () => {
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  const groups = useSpaceTypeGroups();
  const { pathname, search } = location;

  const match = useMemo(() => {
    const matches = routeConfiguration ? matchPathname(pathname, routeConfiguration) : [];
    const first = matches[0];
    return { routeName: first?.route?.name || null, pathParams: first?.params || {} };
  }, [pathname, routeConfiguration]);

  const state = useMemo(
    () =>
      readSearchState({
        routeName: match.routeName,
        pathParams: match.pathParams,
        search,
        groups,
      }),
    [match, search, groups]
  );

  return {
    state,
    groups,
    routeName: match.routeName,
    pathParams: match.pathParams,
    location,
  };
};

export default useSearchState;
